import { Empty, Spin } from "antd";
import { useGetInifiniteConversation } from "../../../hooks/api/Students/Conversations/useGetInifiniteConversation";
import { useEffect, useMemo, useRef, useState } from "react";
import { LoadingOutlined } from "@ant-design/icons";

const ChatBox = ({ id }: { id: string }) => {
  const { data, fetchNextPage, hasNextPage, isLoading } =
    useGetInifiniteConversation({ id });
  const [isScrolling, setIsScrolling] = useState(false);

  const messages = useMemo(
    () => data?.pages.flatMap((page) => page.data.docs) || [],
    [data]
  );

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isScrolling) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isScrolling]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = e.currentTarget;
    setIsScrolling(scrollTop < 0);
    
    // Load more messages when scrolling to top
    if (scrollTop === 0 && hasNextPage) {
      fetchNextPage();
    }
  };

  const highlightLinks = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex).map((part, index) =>
      urlRegex.test(part) ? (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 underline hover:text-blue-300 transition-colors"
        >
          {part}
        </a>
      ) : (
        part
      )
    );
  };

  const formatMessageTime = (date: string) => {
    const now = new Date();
    const messageDate = new Date(date);
    const diffDays = Math.floor((now.getTime() - messageDate.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return messageDate.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });
    } else if (diffDays === 1) {
      return "Yesterday";
    } else {
      return messageDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#1E1B30] rounded-xl overflow-hidden">
      {isLoading ? (
        <div className="flex items-center justify-center h-full">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        </div>
      ) : messages.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-gray-400">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span className="text-gray-300">No messages yet</span>
            }
          />
        </div>
      ) : (
        <div
          ref={chatContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-4 py-6 space-y-4 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent"
        >
          {hasNextPage && (
            <div className="text-center py-2">
              <Spin size="small" />
            </div>
          )}
          
          {messages.slice().reverse().map(({ id, message, sender, deliveredAt }, index) => {
            const isStudent = sender === "Student";
            const isTutor = sender === "Tutor";
            const isAdmin = sender === "Admin";
            const showSenderName = index === 0 || 
              messages[index - 1]?.sender !== sender;

            return (
              <div
                key={id}
                className={`flex ${isStudent ? "justify-end" : "justify-start"} group`}
              >
                <div
                  className={`relative max-w-[70%] ${
                    isAdmin
                      ? "bg-[#333333] text-white w-full"
                      : isStudent
                      ? "bg-[#9B5DE5] text-white"
                      : "bg-[#6A4C93] text-white"
                  } rounded-2xl px-4 py-3 shadow-lg hover:shadow-xl transition-shadow`}
                >
                  {showSenderName && (
                    <p className="text-xs font-medium opacity-75 mb-1">
                      {isAdmin ? "Admin" : isTutor ? "Tutor" : "Student"}
                    </p>
                  )}
                  <p className="text-sm whitespace-pre-wrap break-words">
                    {highlightLinks(message)}
                  </p>
                  {deliveredAt && (
                    <p className="text-[10px] opacity-75 text-right mt-1">
                      {formatMessageTime(deliveredAt)}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      )}
    </div>
  );
};

export default ChatBox;
